import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import { ApiContext } from '../../contexts/ApiContext';
import { useNotification } from '../../contexts/NotificationContext';
import InventoryService from '../../service/inventory.service';
import UserService from '../../service/user.service';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal/ConfirmDeleteModal';
import { formatCentsToBRL, parseBRLToCents } from '../../utils/currency';
import './inventory.css';

const UNITS = [
  ['UNIT', 'Unidade'],
  ['KG', 'Quilograma (kg)'],
  ['G', 'Grama (g)'],
  ['L', 'Litro (L)'],
  ['ML', 'Mililitro (ml)'],
];
const UNIT_LABEL = { UNIT: 'un', KG: 'kg', G: 'g', L: 'L', ML: 'ml' };
const STATUS = {
  NORMAL: { label: 'Normal', color: 'success' },
  LOW: { label: 'Baixo', color: 'warning' },
  ZERO: { label: 'Zerado', color: 'danger' },
  NEGATIVE: { label: 'Negativo', color: 'danger' },
};
const MOVEMENT_TYPES = {
  ENTRY: { label: 'Entrada', color: 'success' },
  MANUAL_OUT: { label: 'Baixa', color: 'danger' },
  ADJUSTMENT: { label: 'Ajuste', color: 'info' },
  ORDER_CONSUMPTION: { label: 'Consumo do pedido', color: 'warning' },
  ORDER_REVERSAL: { label: 'Estorno do pedido', color: 'primary' },
};
const PAGE_SIZE = 20;
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const quantity = (value, unit) =>
  `${number(value).toLocaleString('pt-BR', { maximumFractionDigits: 3 })} ${UNIT_LABEL[unit] || unit}`;
const money = (cents) => formatCentsToBRL(cents);
const maskMoneyInput = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits ? formatCentsToBRL(parseBRLToCents(raw)) : '';
};
const errorMessage = (error, fallback) => {
  const message = error?.response?.data?.message;
  return (Array.isArray(message) ? message.join(', ') : message) || fallback;
};
const today = new Date().toISOString().slice(0, 10);
const thirtyDaysAgo = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="inventory-pagination">
      <Button size="sm" color="secondary" outline disabled={page <= 1} onClick={() => onChange(page - 1)}>
        Anterior
      </Button>
      <span>Página {page} de {totalPages}</span>
      <Button size="sm" color="secondary" outline disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
        Próxima
      </Button>
    </div>
  );
}

function ItemModal({ item, isOpen, toggle, accountId, onSaved }) {
  const { notify } = useNotification();
  const editing = Boolean(item);
  const [form, setForm] = useState({
    name: '',
    unit: 'UNIT',
    currentQuantity: '',
    minimumQuantity: '',
    purchaseValue: '',
    active: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(item
      ? {
          name: item.name || '',
          unit: item.unit,
          currentQuantity: number(item.currentQuantity),
          minimumQuantity: number(item.minimumQuantity),
          purchaseValue: '',
          active: item.active !== false,
        }
      : {
          name: '',
          unit: 'UNIT',
          currentQuantity: '',
          minimumQuantity: '',
          purchaseValue: '',
          active: true,
        });
  }, [item, isOpen]);

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    const initialQty = number(form.currentQuantity);
    const purchaseTotalCents = form.purchaseValue
      ? parseBRLToCents(form.purchaseValue)
      : undefined;
    if (!editing && purchaseTotalCents !== undefined && initialQty <= 0) {
      return notify('tr', 'warning', 'Informe o saldo inicial para registrar o valor da compra', 'icon-alert-circle-exc');
    }
    setSaving(true);
    try {
      if (editing) {
        await InventoryService.updateItem(accountId, item.id, {
          name: form.name.trim(),
          minimumQuantity: number(form.minimumQuantity),
          active: form.active,
        });
      } else {
        await InventoryService.createItem(accountId, {
          name: form.name.trim(),
          unit: form.unit,
          currentQuantity: initialQty,
          minimumQuantity: number(form.minimumQuantity),
          ...(purchaseTotalCents !== undefined && { purchaseTotalCents }),
        });
      }
      notify('tr', 'success', editing ? 'Item atualizado' : 'Item cadastrado', 'icon-check-2');
      toggle();
      onSaved();
    } catch (error) {
      notify('tr', 'danger', errorMessage(error, 'Não foi possível salvar o item'), 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="inventory-modal">
      <ModalHeader toggle={toggle}>{editing ? 'Editar item' : 'Cadastrar item'}</ModalHeader>
      <Form onSubmit={save}>
        <ModalBody>
          <FormGroup>
            <Label>Nome</Label>
            <Input autoFocus required maxLength={100} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </FormGroup>
          {!editing && (
            <>
              <Row>
                <Col sm="6">
                  <FormGroup>
                    <Label>Unidade de controle</Label>
                    <Input type="select" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
                      {UNITS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                    </Input>
                  </FormGroup>
                </Col>
                <Col sm="6">
                  <FormGroup>
                    <Label>Saldo inicial</Label>
                    <Input type="number" min="0" step="any" value={form.currentQuantity} onChange={(e) => setForm({ ...form, currentQuantity: e.target.value })} />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label>Valor total da compra</Label>
                <Input
                  inputMode="numeric"
                  placeholder="R$ 0,00 (opcional)"
                  value={form.purchaseValue}
                  onChange={(e) => setForm({ ...form, purchaseValue: maskMoneyInput(e.target.value) })}
                />
                <small className="text-muted">Opcional. Usado para calcular o custo médio do estoque.</small>
              </FormGroup>
            </>
          )}
          <FormGroup>
            <Label>Estoque mínimo ({UNIT_LABEL[form.unit]})</Label>
            <Input required type="number" min="0" step="any" value={form.minimumQuantity} onChange={(e) => setForm({ ...form, minimumQuantity: e.target.value })} />
          </FormGroup>
          {editing && (
            <FormGroup check className="inventory-check">
              <Label check>
                <Input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />{' '}
                Item ativo
              </Label>
              <small className="text-muted d-block">Itens inativos continuam no histórico, mas não podem ser movimentados.</small>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter className="inventory-modal-footer">
          <Button type="button" color="secondary" outline onClick={toggle}>Cancelar</Button>
          <Button type="submit" color="primary" disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function MovementModal({ item, isOpen, toggle, accountId, onSaved }) {
  const { notify } = useNotification();
  const [form, setForm] = useState({ type: 'ENTRY', quantity: '', purchaseValue: '', reason: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({ type: 'ENTRY', quantity: '', purchaseValue: '', reason: '' });
  }, [item, isOpen]);

  if (!item) return null;
  const current = number(item.currentQuantity);
  const entered = number(String(form.quantity).replace(',', '.'));
  const finalBalance = form.type === 'ENTRY' ? current + entered : form.type === 'MANUAL_OUT' ? current - entered : entered;
  const reasonRequired = form.type !== 'ENTRY';

  const save = async (event) => {
    event.preventDefault();
    if ((form.type !== 'ADJUSTMENT' && entered <= 0) || entered < 0) {
      return notify('tr', 'warning', 'Informe uma quantidade válida', 'icon-alert-circle-exc');
    }
    if (reasonRequired && !form.reason.trim()) {
      return notify('tr', 'warning', 'Informe o motivo da movimentação', 'icon-alert-circle-exc');
    }
    const purchaseTotalCents = form.purchaseValue
      ? parseBRLToCents(form.purchaseValue)
      : undefined;
    setSaving(true);
    try {
      await InventoryService.createMovement(accountId, item.id, {
        type: form.type,
        quantity: entered,
        ...(form.type === 'ENTRY' && purchaseTotalCents !== undefined && { purchaseTotalCents }),
        ...(form.reason.trim() && { reason: form.reason.trim() }),
      });
      notify('tr', 'success', 'Movimentação registrada', 'icon-check-2');
      toggle();
      onSaved();
    } catch (error) {
      notify('tr', 'danger', errorMessage(error, 'Não foi possível registrar a movimentação'), 'icon-alert-circle-exc');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="inventory-modal">
      <ModalHeader toggle={toggle}>Movimentar estoque</ModalHeader>
      <Form onSubmit={save}>
        <ModalBody>
          <div className="inventory-modal-item">
            <strong>{item.name}</strong>
            <span>Saldo atual: {quantity(current, item.unit)}</span>
          </div>
          <FormGroup>
            <Label>Operação</Label>
            <Input type="select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value, quantity: '', purchaseValue: '', reason: '' })}>
              <option value="ENTRY">Entrada</option>
              <option value="MANUAL_OUT">Baixa</option>
              <option value="ADJUSTMENT">Ajuste de saldo</option>
            </Input>
          </FormGroup>
          <Row>
            <Col sm={form.type === 'ENTRY' ? 6 : 12}>
              <FormGroup>
                <Label>{form.type === 'ADJUSTMENT' ? 'Novo saldo' : 'Quantidade'} ({UNIT_LABEL[item.unit]})</Label>
                <Input autoFocus required type="number" min="0" step="any" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </FormGroup>
            </Col>
            {form.type === 'ENTRY' && (
              <Col sm="6">
                <FormGroup>
                  <Label>Valor total da compra</Label>
                  <Input
                    inputMode="numeric"
                    placeholder="R$ 0,00 (opcional)"
                    value={form.purchaseValue}
                    onChange={(e) => setForm({ ...form, purchaseValue: maskMoneyInput(e.target.value) })}
                  />
                </FormGroup>
              </Col>
            )}
          </Row>
          {reasonRequired && (
            <FormGroup>
              <Label>Motivo</Label>
              <Input required maxLength={250} placeholder={form.type === 'ADJUSTMENT' ? 'Ex.: contagem física' : 'Ex.: perda ou uso interno'} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </FormGroup>
          )}
          <div className={`inventory-balance-preview ${finalBalance < 0 ? 'is-negative' : ''}`}>
            <span>Saldo após a movimentação</span>
            <strong>{quantity(finalBalance, item.unit)}</strong>
          </div>
        </ModalBody>
        <ModalFooter className="inventory-modal-footer">
          <Button type="button" color="secondary" outline onClick={toggle}>Cancelar</Button>
          <Button type="submit" color="primary" disabled={saving}>{saving ? 'Registrando...' : 'Registrar'}</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function InventoryPage() {
  const { account, setAccount } = useContext(ApiContext);
  const { notify } = useNotification();
  const accountId = account?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'movements' ? 'movements' : 'items');
  const [overview, setOverview] = useState(null);
  const [itemsResult, setItemsResult] = useState({ data: [], page: 1, totalPages: 0, total: 0 });
  const [itemsLoading, setItemsLoading] = useState(true);
  const [itemsError, setItemsError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [unit, setUnit] = useState('');
  const [active, setActive] = useState('true');
  const [itemPage, setItemPage] = useState(1);
  const [allItems, setAllItems] = useState([]);
  const [itemModal, setItemModal] = useState({ open: false, item: null });
  const [movementModal, setMovementModal] = useState({ open: false, item: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });
  const [movements, setMovements] = useState({ data: [], page: 1, totalPages: 0, total: 0 });
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsError, setMovementsError] = useState('');
  const [movementPage, setMovementPage] = useState(1);
  const [movementFilters, setMovementFilters] = useState({
    from: thirtyDaysAgo,
    to: today,
    inventoryItemId: '',
    type: '',
    userId: '',
    orderId: '',
  });
  const [users, setUsers] = useState([]);
  const [policySaving, setPolicySaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setItemPage(1), [debouncedSearch, status, unit, active]);

  const loadOverview = useCallback(async () => {
    if (!accountId) return;
    try {
      setOverview(await InventoryService.getOverview(accountId));
    } catch (error) {
      console.error('Falha ao carregar resumo do estoque', error);
    }
  }, [accountId]);

  const loadItemOptions = useCallback(async () => {
    if (!accountId) return;
    try {
      const firstPage = await InventoryService.listItems(accountId, { page: 1, size: 100 });
      const remaining = (firstPage?.totalPages || 1) > 1
        ? await Promise.all(
            Array.from({ length: firstPage.totalPages - 1 }, (_, index) =>
              InventoryService.listItems(accountId, { page: index + 2, size: 100 })
            )
          )
        : [];
      setAllItems([...(firstPage?.data || []), ...remaining.flatMap((page) => page?.data || [])]);
    } catch (error) {
      console.error('Falha ao carregar opções de estoque', error);
    }
  }, [accountId]);

  const loadItems = useCallback(async () => {
    if (!accountId) return;
    setItemsLoading(true);
    setItemsError('');
    try {
      const result = await InventoryService.listItems(accountId, {
        page: itemPage,
        size: PAGE_SIZE,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(status && { status }),
        ...(unit && { unit }),
        ...(active !== '' && { active }),
      });
      setItemsResult(result || { data: [], page: 1, totalPages: 0, total: 0 });
    } catch (error) {
      setItemsError(errorMessage(error, 'Não foi possível carregar os itens.'));
    } finally {
      setItemsLoading(false);
    }
  }, [accountId, itemPage, debouncedSearch, status, unit, active]);

  const loadMovements = useCallback(async () => {
    if (!accountId || tab !== 'movements') return;
    setMovementsLoading(true);
    setMovementsError('');
    try {
      const result = await InventoryService.listMovements(accountId, {
        page: movementPage,
        size: PAGE_SIZE,
        ...(movementFilters.inventoryItemId && { inventoryItemId: movementFilters.inventoryItemId }),
        ...(movementFilters.type && { type: movementFilters.type }),
        ...(movementFilters.userId && { userId: movementFilters.userId }),
        ...(movementFilters.orderId && { orderId: movementFilters.orderId }),
        ...(movementFilters.from && { from: `${movementFilters.from}T00:00:00` }),
        ...(movementFilters.to && { to: `${movementFilters.to}T23:59:59.999` }),
      });
      setMovements(result || { data: [], page: 1, totalPages: 0, total: 0 });
    } catch (error) {
      setMovementsError(errorMessage(error, 'Não foi possível carregar as movimentações.'));
    } finally {
      setMovementsLoading(false);
    }
  }, [accountId, tab, movementPage, movementFilters]);

  useEffect(() => { loadOverview(); loadItemOptions(); }, [loadOverview, loadItemOptions]);
  useEffect(() => {
    if (!accountId) return;
    UserService.list(accountId)
      .then((result) => setUsers(Array.isArray(result) ? result : result?.data || []))
      .catch(() => setUsers([]));
  }, [accountId]);
  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadMovements(); }, [loadMovements]);

  const reload = () => {
    loadOverview();
    loadItems();
    loadItemOptions();
    if (tab === 'movements') loadMovements();
  };
  const changeTab = (next) => {
    setTab(next);
    setSearchParams(next === 'movements' ? { tab: 'movements' } : status ? { status } : {});
  };
  const updateMovementFilter = (key, value) => {
    setMovementPage(1);
    setMovementFilters((current) => ({ ...current, [key]: value }));
  };
  const updatePolicy = async (inventoryPolicy) => {
    setPolicySaving(true);
    try {
      await InventoryService.updateSettings(accountId, { inventoryPolicy });
      setAccount({ ...account, inventoryPolicy });
      notify('tr', 'success', 'Política de estoque atualizada', 'icon-check-2');
    } catch (error) {
      notify('tr', 'danger', errorMessage(error, 'Não foi possível alterar a política'), 'icon-alert-circle-exc');
    } finally {
      setPolicySaving(false);
    }
  };

  const confirmDelete = async () => {
    const item = deleteModal.item;
    if (!item) return;
    try {
      const result = await InventoryService.deleteItem(accountId, item.id);
      const recipes = result?.recipesRemoved || [];
      const uniqueMenus = [...new Set(recipes.map((entry) => entry.menuItemName))];
      const recipeMsg = uniqueMenus.length
        ? ` Removido das fichas: ${uniqueMenus.join(', ')}.`
        : '';
      notify(
        'tr',
        'success',
        `${result?.message || 'Item excluído.'}${recipeMsg}`,
        'icon-check-2'
      );
      reload();
    } catch (error) {
      notify('tr', 'danger', errorMessage(error, 'Não foi possível excluir o item'), 'icon-alert-circle-exc');
    }
  };

  const summary = useMemo(() => [
    ['Itens ativos', overview?.totalItems ?? '—', 'icon-basket-simple', 'primary'],
    ['Estoque baixo', overview?.lowItems ?? '—', 'icon-alert-circle-exc', 'warning'],
    ['Zerados', overview?.zeroItems ?? '—', 'icon-simple-remove', 'danger'],
    ['Negativos', overview?.negativeItems ?? '—', 'icon-minimal-down', 'danger'],
    ['Valor estimado', overview ? money(overview.totalValueCents) : '—', 'icon-money-coins', 'success'],
  ], [overview]);

  return (
    <div className="content inventory-page">
      <Row className="align-items-center mb-3">
        <Col md="8">
          <h2 className="mb-1">Gestão de estoque</h2>
          <p className="text-muted mb-0">Controle insumos, custos e movimentações em um só lugar.</p>
        </Col>
        <Col md="4" className="text-md-right mt-3 mt-md-0">
          <Button color="primary" onClick={() => setItemModal({ open: true, item: null })}>
            <i className="tim-icons icon-simple-add mr-1" /> Cadastrar item
          </Button>
        </Col>
      </Row>

      <Row className="inventory-summary-row">
        {summary.map(([label, value, icon, color]) => (
          <Col key={label} xl md="4" sm="6">
            <Card className="inventory-summary-card">
              <CardBody>
                <div className={`inventory-summary-icon text-${color}`}><i className={`tim-icons ${icon}`} /></div>
                <div><small>{label}</small><strong>{value}</strong></div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <CardHeader className="inventory-card-header">
          <div className="inventory-tab-switch" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'items'}
              className={`inventory-tab-btn ${tab === 'items' ? 'is-active' : ''}`}
              onClick={() => changeTab('items')}
            >
              Itens
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'movements'}
              className={`inventory-tab-btn ${tab === 'movements' ? 'is-active' : ''}`}
              onClick={() => changeTab('movements')}
            >
              Movimentações
            </button>
          </div>
        </CardHeader>
        <CardBody>
          {tab === 'items' ? (
            <>
              <div className="inventory-filters">
                <Input placeholder="Buscar item..." value={search} onChange={(e) => setSearch(e.target.value)} />
                <Input type="select" value={status} onChange={(e) => { setStatus(e.target.value); setSearchParams(e.target.value ? { status: e.target.value } : {}); }}>
                  <option value="">Todos os status</option>
                  {Object.entries(STATUS).map(([value, meta]) => <option value={value} key={value}>{meta.label}</option>)}
                </Input>
                <Input type="select" value={unit} onChange={(e) => setUnit(e.target.value)}>
                  <option value="">Todas as unidades</option>
                  {UNITS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                </Input>
                <Input type="select" value={active} onChange={(e) => setActive(e.target.value)}>
                  <option value="">Ativos e inativos</option>
                  <option value="true">Somente ativos</option>
                  <option value="false">Somente inativos</option>
                </Input>
              </div>
              {itemsLoading ? (
                <div className="inventory-state"><Spinner color="primary" /><span>Carregando itens...</span></div>
              ) : itemsError ? (
                <div className="inventory-state"><i className="tim-icons icon-alert-circle-exc" /><span>{itemsError}</span><Button size="sm" color="primary" outline onClick={loadItems}>Tentar novamente</Button></div>
              ) : !itemsResult.data?.length ? (
                <div className="inventory-state"><i className="tim-icons icon-basket-simple" /><strong>Nenhum item encontrado</strong><span className="text-muted">Ajuste os filtros ou cadastre o primeiro item.</span></div>
              ) : (
                <>
                  <div className="inventory-desktop-table">
                    <Table responsive className="tablesorter mb-0">
                      <thead className="text-primary"><tr><th>Item</th><th>Saldo</th><th>Mínimo</th><th>Status</th><th>Custo médio</th><th>Estimativa</th><th className="text-right">Ações</th></tr></thead>
                      <tbody>
                        {itemsResult.data.map((item) => (
                          <tr key={item.id} className={!item.active ? 'inventory-inactive' : ''}>
                            <td><strong>{item.name}</strong>{!item.active && <small className="d-block text-muted">Inativo</small>}</td>
                            <td>{quantity(item.currentQuantity, item.unit)}</td>
                            <td>{quantity(item.minimumQuantity, item.unit)}</td>
                            <td><Badge color={STATUS[item.status]?.color || 'secondary'}>{STATUS[item.status]?.label || item.status}</Badge></td>
                            <td>{money(item.averageCostCents)}</td>
                            <td>{item.daysRemaining == null ? '—' : `${number(item.daysRemaining).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} dias`}</td>
                            <td className="text-right">
                              <Button className="btn-icon mr-2" size="sm" color="info" title="Editar" onClick={() => setItemModal({ open: true, item })}><i className="tim-icons icon-pencil" /></Button>
                              <Button className="btn-icon mr-2" size="sm" color="danger" title="Excluir" onClick={() => setDeleteModal({ open: true, item })}><i className="tim-icons icon-trash-simple" /></Button>
                              <Button size="sm" color="primary" disabled={!item.active} onClick={() => setMovementModal({ open: true, item })}>Movimentar</Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <div className="inventory-mobile-list">
                    {itemsResult.data.map((item) => (
                      <div className={`inventory-mobile-card ${!item.active ? 'inventory-inactive' : ''}`} key={item.id}>
                        <div className="inventory-mobile-head"><strong>{item.name}</strong><Badge color={STATUS[item.status]?.color || 'secondary'}>{STATUS[item.status]?.label || item.status}</Badge></div>
                        <div className="inventory-mobile-grid"><span>Saldo <b>{quantity(item.currentQuantity, item.unit)}</b></span><span>Mínimo <b>{quantity(item.minimumQuantity, item.unit)}</b></span><span>Custo médio <b>{money(item.averageCostCents)}</b></span><span>Estimativa <b>{item.daysRemaining == null ? '—' : `${number(item.daysRemaining).toFixed(1)} dias`}</b></span></div>
                        <div className="inventory-mobile-actions">
                          <Button size="sm" color="secondary" outline onClick={() => setItemModal({ open: true, item })}>Editar</Button>
                          <Button size="sm" color="danger" outline onClick={() => setDeleteModal({ open: true, item })}>Excluir</Button>
                          <Button size="sm" color="primary" disabled={!item.active} onClick={() => setMovementModal({ open: true, item })}>Movimentar</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Pagination page={itemsResult.page} totalPages={itemsResult.totalPages} onChange={setItemPage} />
                </>
              )}

              <div className="inventory-policy">
                <div>
                  <strong>Comportamento sem saldo</strong>
                  <p className="mb-0 text-muted">No pedido presencial, bloquear impede a confirmação sem estoque. Pedidos ONLINE apenas exibem alerta para não interromper a venda.</p>
                </div>
                <div className="inventory-policy-options">
                  <Button size="sm" color="warning" outline={account?.inventoryPolicy !== 'WARN'} disabled={policySaving} onClick={() => updatePolicy('WARN')}>WARN · Apenas alertar</Button>
                  <Button size="sm" color="danger" outline={account?.inventoryPolicy !== 'BLOCK'} disabled={policySaving} onClick={() => updatePolicy('BLOCK')}>BLOCK · Bloquear presencial</Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="inventory-filters inventory-movement-filters">
                <Input type="date" aria-label="Data inicial" value={movementFilters.from} onChange={(e) => updateMovementFilter('from', e.target.value)} />
                <Input type="date" aria-label="Data final" value={movementFilters.to} onChange={(e) => updateMovementFilter('to', e.target.value)} />
                <Input type="select" value={movementFilters.inventoryItemId} onChange={(e) => updateMovementFilter('inventoryItemId', e.target.value)}>
                  <option value="">Todos os itens</option>
                  {allItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </Input>
                <Input type="select" value={movementFilters.type} onChange={(e) => updateMovementFilter('type', e.target.value)}>
                  <option value="">Todos os tipos</option>
                  {Object.entries(MOVEMENT_TYPES).map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}
                </Input>
                <Input type="select" value={movementFilters.userId} onChange={(e) => updateMovementFilter('userId', e.target.value)}>
                  <option value="">Todos os usuários</option>
                  {users.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                </Input>
                <Input type="number" min="1" placeholder="Número do pedido" value={movementFilters.orderId} onChange={(e) => updateMovementFilter('orderId', e.target.value)} />
              </div>
              {movementsLoading ? (
                <div className="inventory-state"><Spinner color="primary" /><span>Carregando movimentações...</span></div>
              ) : movementsError ? (
                <div className="inventory-state"><i className="tim-icons icon-alert-circle-exc" /><span>{movementsError}</span><Button size="sm" color="primary" outline onClick={loadMovements}>Tentar novamente</Button></div>
              ) : !movements.data?.length ? (
                <div className="inventory-state"><i className="tim-icons icon-refresh-01" /><strong>Nenhuma movimentação encontrada</strong><span className="text-muted">Não há registros para os filtros selecionados.</span></div>
              ) : (
                <>
                  <div className="inventory-desktop-table">
                    <Table responsive className="tablesorter mb-0">
                      <thead className="text-primary"><tr><th>Data</th><th>Item</th><th>Tipo</th><th>Variação</th><th>Antes / depois</th><th>Custo / motivo</th><th>Usuário</th><th>Pedido</th></tr></thead>
                      <tbody>
                        {movements.data.map((movement) => {
                          const unit = movement.inventoryItem?.unit;
                          return (
                            <tr key={movement.id}>
                              <td>{new Date(movement.createdAt).toLocaleString('pt-BR')}</td>
                              <td><strong>{movement.inventoryItem?.name || '—'}</strong></td>
                              <td><Badge color={MOVEMENT_TYPES[movement.type]?.color || 'secondary'}>{MOVEMENT_TYPES[movement.type]?.label || movement.type}</Badge></td>
                              <td className={number(movement.delta) < 0 ? 'text-danger' : 'text-success'}>{number(movement.delta) > 0 ? '+' : ''}{quantity(movement.delta, unit)}</td>
                              <td>{quantity(movement.balanceBefore, unit)} → {quantity(movement.balanceAfter, unit)}</td>
                              <td>{movement.purchaseTotalCents != null ? money(movement.purchaseTotalCents) : movement.reason || '—'}</td>
                              <td>{movement.user?.name || 'Sistema'}</td>
                              <td>{movement.orderId ? `#${movement.orderId}` : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                  <div className="inventory-mobile-list">
                    {movements.data.map((movement) => {
                      const unit = movement.inventoryItem?.unit;
                      return (
                        <div className="inventory-mobile-card" key={movement.id}>
                          <div className="inventory-mobile-head"><strong>{movement.inventoryItem?.name}</strong><Badge color={MOVEMENT_TYPES[movement.type]?.color || 'secondary'}>{MOVEMENT_TYPES[movement.type]?.label || movement.type}</Badge></div>
                          <small className="text-muted">{new Date(movement.createdAt).toLocaleString('pt-BR')}</small>
                          <div className="inventory-movement-balance"><strong className={number(movement.delta) < 0 ? 'text-danger' : 'text-success'}>{number(movement.delta) > 0 ? '+' : ''}{quantity(movement.delta, unit)}</strong><span>{quantity(movement.balanceBefore, unit)} → {quantity(movement.balanceAfter, unit)}</span></div>
                          <div className="inventory-movement-meta"><span>{movement.purchaseTotalCents != null ? money(movement.purchaseTotalCents) : movement.reason || 'Sem motivo'}</span><span>{movement.user?.name || 'Sistema'}{movement.orderId ? ` · Pedido #${movement.orderId}` : ''}</span></div>
                        </div>
                      );
                    })}
                  </div>
                  <Pagination page={movements.page} totalPages={movements.totalPages} onChange={setMovementPage} />
                </>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <ItemModal item={itemModal.item} isOpen={itemModal.open} toggle={() => setItemModal({ open: false, item: null })} accountId={accountId} onSaved={reload} />
      <MovementModal item={movementModal.item} isOpen={movementModal.open} toggle={() => setMovementModal({ open: false, item: null })} accountId={accountId} onSaved={reload} />
      <ConfirmDeleteModal
        isOpen={deleteModal.open}
        toggle={() => setDeleteModal({ open: false, item: null })}
        title="Excluir item de estoque"
        message={
          deleteModal.item
            ? `Excluir "${deleteModal.item.name}"? Se estiver em fichas técnicas do cardápio, será removido delas automaticamente. Itens com histórico serão apenas desativados.`
            : 'Tem certeza?'
        }
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default InventoryPage;
